import {
    AfterViewInit,
    ApplicationRef, Component,
    ComponentRef,
    createComponent, Directive,
    EventEmitter,
    inject,
    Injectable, input, signal,
    Type
} from '@angular/core';
import {Popup, PopupTrigger} from '@mescius/wijmo.input';
import {toMerged} from 'es-toolkit';

export interface DynamicComponent {
    notifyInitComplete: EventEmitter<DynamicComponent>;
    componentRef: ComponentRef<DynamicComponent> | undefined
}

export interface DynamicComponentOptions<T = any> {
    hostElement?: Element;
    input?: T;
}

export interface DialogContainerOptions<O> extends DynamicComponentOptions<O> {
    titleIconClass?: string;
    title?: string;
    width?: string;
    height?: string;
    showCloseButton?: boolean;
}


@Injectable()
@Component({ template: ''})
export class DynamicComponentBase implements DynamicComponent, AfterViewInit {
    notifyInitComplete = new EventEmitter<DynamicComponent>();
    componentRef: ComponentRef<DynamicComponent> | undefined;

    dynPopup = input<Popup|undefined>(undefined);

    ngAfterViewInit(): void {
        this.notifyInitComplete.emit(this);
    }


    /**
     * Checks if the given component type is a subclass of DynamicComponentBase.
     * @param component The component type to check.
     */
    static isDynamicComponentBaseSubclass<T>(component: Type<T>): boolean {
        return DynamicComponentBase.prototype.isPrototypeOf(component.prototype);
    }
}

@Injectable({
    providedIn: 'root'
})
export class NgDynamicComponentsService {

    private applicationRef = inject(ApplicationRef);

    public static instance: NgDynamicComponentsService;

    constructor() {
        NgDynamicComponentsService.instance = this;
    }

    /**
     * Creates a dynamic component based on the provided component type.
     *
     * @param {Type<T>} component - The component type to create.
     * @param {DynamicComponentOptions<O>} [options] - Optional configuration for the component.
     * @returns {Promise<ComponentRef<T>>} - A promise that resolves with the component reference after it has been initialized.
     * @private
     */
    async createComponent<T extends DynamicComponent, O>(component: Type<T>, options?: DynamicComponentOptions<O>): Promise<ComponentRef<T>> {
        let hostContainerElement = options?.hostElement;
        if (!hostContainerElement) {
            const hostContainer = document.getElementById('app-popup-host') ?? document.body
            hostContainerElement = document.createElement('div');
            hostContainer.appendChild(hostContainerElement);
        }

        const environmentInjector = this.applicationRef.injector;
        const componentRef = createComponent(component, {
            environmentInjector,
            hostElement: hostContainerElement
        });

        // iterate over all properties in input
        // and set them on the component instance
        const inputs = options?.input;
        for (const key in inputs) {
            componentRef.setInput(key, inputs[key]);
        }

        this.applicationRef.attachView(componentRef.hostView);

        componentRef.instance.componentRef = componentRef;
        return new Promise((resolve, reject) => {
            componentRef.instance.notifyInitComplete.subscribe(() => resolve(componentRef));
        });
    }

    async createComponentDialog<T extends DynamicComponent, O>(component: Type<T>, options?: DialogContainerOptions<O>) : Promise<ComponentRef<T>> {
        const showCloseButton = options?.showCloseButton ?? true;

        const divEl = document.createElement('div');
        divEl.style.width = options?.width ?? '500px';
        divEl.style.height = options?.height ?? '400px';

        const bodyHtml = `
            <div class="dialog-header modal-header">
              <h2><span class="placeholder-dialog-icon"></span> <span class="placeholder-dialog-title"></span></h2>
            </div>
            <div class="dialog-content layout-vbox">
            </div>
        `;

        const footer = `
            <div class="dialog-footer button-hbox">
                <fluent-button appearance="primary" class="close wj-hide">Close</fluent-button>
            </div>
        `;

        divEl.innerHTML = `
            <div class="fullsize-vbox wijmo-dialog">
                ${bodyHtml}
                ${showCloseButton ? footer : ''}
            </div>`;

        const iconEl = divEl.querySelector('.placeholder-dialog-icon')!;
        const titleEl = divEl.querySelector('.placeholder-dialog-title')!;
        const contentEl = divEl.querySelector('.dialog-content')!;

        if (options?.titleIconClass) {
            iconEl.classList.add(...options.titleIconClass.split(' '));
        }

        if (options?.title) {
            titleEl.textContent = options.title;
        }

        const frmLogin = new Popup(divEl, {
            isDraggable: true,
            isResizable: true,
            fadeIn: false,
            fadeOut: false,
            showTrigger: PopupTrigger.None,
            hideTrigger: PopupTrigger.None,
            removeOnHide: true
        });

        // merge the options.input and, when applicable, add the dynamic popup input
        // this allows the component to access the popup for hide/show functionality
        const componentInput: any = toMerged({}, options?.input ?? {});
        if (DynamicComponentBase.isDynamicComponentBaseSubclass(component)) {
            componentInput.dynPopup = frmLogin;
        }

        const componentOptions = {
            hostElement: contentEl,
            input: componentInput
        };
        const comp = await this.createComponent(component, componentOptions);

        frmLogin.hidden.addHandler(() => {
            comp.destroy();
        });

        frmLogin.show(true);
        return comp;
    }

}
